import { useEffect, useState } from "react";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "../../hooks";
import { toSurfaceTint } from "../../utils/colorManager";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { formatCurrency } from "../../utils";
import useMaskedInput from "../../hooks/useMaskedInput";
import { displayAmount } from "../../utils/displayMask";
import {
  amountKeystrokes,
  exceedsServiceMax,
  isWithinFee,
  toMinorUnits,
} from "../../utils/schoolFeeAmount";
import type { ServiceAmountBounds } from "../../utils/schoolFeeAmount";
import { amountRules } from "../../utils/amountRules";
import { currencyAdornment } from "../inputs/adornments";

/**
 * PAY-06 / PAY-07. The amount the payer will be charged.
 *
 * ⛔ `amount` is MANDATORY on the pay request (D-HARM-16) -- the server no
 * longer reads an absent amount as "the full fee", so the form always carries a
 * value: the fee itself unless the payer chooses to pay part of it, rather
 * than relying on a default that does not exist any more.
 *
 * ⛔ Nothing here parses the money into a float to decide whether it may be
 * paid. `isWithinFee` compares integer minor units, because a fee ceiling
 * compared as a float is an approximate ceiling and the ceiling is the only
 * thing between a partial payment and an overpayment.
 */
const SchoolFeeAmountForm = ({
  serviceBounds,
}: {
  serviceBounds?: ServiceAmountBounds;
} = {}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { selectedFee, amount, setAmount } = useSchoolFee();

  const fee = selectedFee?.amount ?? "";
  // ⚠ Only an EXPLICIT false refuses a partial payment. Journey A's fees come
  // from the student-search hit and always carry the flag; journey B's come
  // from /schoolfee/fee-assignment, which carries it from customer portal API
  // 2.1.0 and omits it before. Reading undefined as false would block every
  // partial payment on an older server, on no evidence that the school
  // forbids one -- and the server refuses a partial it does not allow
  // (CPS-422-111) either way. Unknown therefore defers to the server.
  const partialAllowed = selectedFee?.supports_partial_payments !== false;

  // Full is the default. Coming back with a part amount already typed reopens
  // "Pay part of it"; selectFee clears the amount when the fee changes.
  const [mode, setMode] = useState<"full" | "part">(() =>
    partialAllowed && amount !== "" && amount !== fee ? "part" : "full"
  );
  const payingFull = mode === "full" || !partialAllowed;

  // The amount carried in full mode is always the fee itself, so the pay
  // request never lacks one (D-HARM-16) and no box can drift from it.
  useEffect(() => {
    if (payingFull && fee && amount !== fee) setAmount(fee);
  }, [payingFull, fee, amount, setAmount]);

  const chooseMode = (next: "full" | "part") => {
    setMode(next);
    setAmount(next === "full" ? fee : "");
  };

  const invalid = amount !== "" && !isWithinFee(amount, fee, serviceBounds);
  // Filtered at the keystroke rather than corrected on submit: a third
  // decimal place is refused by the server, and the payer should see that.
  const masked = useMaskedInput({
    value: amount,
    clean: amountKeystrokes,
    display: displayAmount,
    onValueChange: setAmount,
  });

  const entered = amount !== "" ? toMinorUnits(amount) : null;
  const feeCeiling = toMinorUnits(fee);
  const serviceMin =
    typeof serviceBounds?.min === "number" && serviceBounds.min > 0
      ? serviceBounds.min
      : undefined;
  const serviceMax =
    typeof serviceBounds?.max === "number" && serviceBounds.max > 0
      ? serviceBounds.max
      : undefined;

  const exceedsFee =
    invalid && entered !== null && feeCeiling !== null && entered > feeCeiling;
  const belowServiceMin =
    invalid &&
    !exceedsFee &&
    entered !== null &&
    serviceMin !== undefined &&
    entered < Math.round(serviceMin * 100);
  const aboveServiceMax =
    invalid &&
    !exceedsFee &&
    !belowServiceMin &&
    entered !== null &&
    serviceMax !== undefined &&
    entered > Math.round(serviceMax * 100);

  const { multiple } = amountRules(serviceBounds);
  const money = (value: number) =>
    formatCurrency(value, undefined, selectedFee?.currency);
  const notMultiple =
    invalid &&
    !exceedsFee &&
    !belowServiceMin &&
    !aboveServiceMax &&
    entered !== null &&
    multiple !== null &&
    entered % Math.round(multiple * 100) !== 0;

  // Only what the service declared; a service with no settings shows no line.
  const settingsHint =
    multiple !== null && serviceMin !== undefined
      ? t("schoolfee.amount.settings_multiple_min", {
          multiple: money(multiple),
          min: money(serviceMin),
        })
      : multiple !== null
      ? t("schoolfee.amount.settings_multiple", { multiple: money(multiple) })
      : serviceMin !== undefined
      ? t("schoolfee.amount.settings_min", { min: money(serviceMin) })
      : null;

  const helper = belowServiceMin
    ? t("schoolfee.amount.below_service_min", {
        min: formatCurrency(serviceMin ?? 0, undefined, selectedFee?.currency),
      })
    : aboveServiceMax
    ? t("schoolfee.amount.above_service_max", {
        max: formatCurrency(serviceMax ?? 0, undefined, selectedFee?.currency),
      })
    : notMultiple
    ? t("schoolfee.amount.not_multiple", { multiple: money(multiple ?? 0) })
    : invalid && !exceedsFee
    ? t("schoolfee.amount.invalid")
    : // Above the fee the refusal names the amount owed as the maximum.
      t("schoolfee.amount.maximum", {
        max: formatCurrency(fee, undefined, selectedFee?.currency),
      });

  // The full fee is exempt from the minimum and the multiple, never from the
  // maximum; with no amount box to carry a helper, the refusal is said here.
  const fullAboveServiceMax =
    payingFull && fee !== "" && exceedsServiceMax(fee, serviceBounds);

  // The full amount sits against the row's right edge, where the dummy puts
  // it; "pay part of it" has no amount until the payer types one.
  const option = (value: "full" | "part", label: string, amount?: string) => (
    <FormControlLabel
      value={value}
      control={<Radio />}
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
            {label}
          </Typography>
          {amount && (
            <Typography
              sx={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {amount}
            </Typography>
          )}
        </Box>
      }
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: mode === value ? theme.color_primary : "divider",
        // The chosen row carries the brand's own tint, as on the Fee step.
        bgcolor:
          mode === value
            ? toSurfaceTint(theme.color_secondary)
            : "background.paper",
        "& .MuiFormControlLabel-label": { flex: 1, minWidth: 0 },
      }}
    />
  );

  return (
    <Box mb={4}>
      {/* The dummy's fee card: what is being paid, above the amount choice.
          It also replaces the step heading that used to sit here, which
          repeated the journey card's own title verbatim. */}
      {selectedFee && (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 3,
            p: 1.75,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {t("schoolfee.fee.select_label")}
          </Typography>
          <Typography
            sx={{ fontWeight: 600, mt: 0.25, overflowWrap: "anywhere" }}
          >
            {fee !== ""
              ? `${selectedFee.name} · ${formatCurrency(
                  fee,
                  undefined,
                  selectedFee.currency
                )}`
              : selectedFee.name}
          </Typography>
        </Box>
      )}
      {!partialAllowed ? (
        <Typography sx={{ fontSize: 13 }}>
          {t("schoolfee.amount.partial_unsupported")}
        </Typography>
      ) : (
        <RadioGroup
          value={mode}
          onChange={event => chooseMode(event.target.value as "full" | "part")}
          sx={{ gap: 1.5 }}
        >
          {option(
            "full",
            t("schoolfee.amount.full"),
            fee !== ""
              ? formatCurrency(fee, undefined, selectedFee?.currency)
              : undefined
          )}
          {option("part", t("schoolfee.amount.partial"))}
        </RadioGroup>
      )}
      {fullAboveServiceMax && (
        <Typography role="alert" color="error" sx={{ fontSize: 13, mt: 1.5 }}>
          {t("schoolfee.amount.full_above_service_max", {
            max: formatCurrency(serviceMax ?? 0, undefined, selectedFee?.currency),
          })}
        </Typography>
      )}
      {!payingFull && (
        <Box mt={2}>
          {settingsHint && (
            <Typography sx={{ fontSize: 13, mb: 1.5 }}>{settingsHint}</Typography>
          )}
          <TextInput
            fullWidth
            type="text"
            inputMode="decimal"
            id="schoolfee-amount"
            slotProps={currencyAdornment(selectedFee?.currency)}
            label={t("schoolfee.amount.label")}
            inputRef={masked.inputRef}
            value={masked.value}
            onChange={masked.onChange}
            error={invalid}
            helperText={helper}
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
};

export default SchoolFeeAmountForm;
